import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, deleteDoc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { FirebaseUser } from '../types/user';
import './stylesheets/UserManagement.css';
import SelectedUserManagementModal from '../components/SelectedUserManagementModal';
import EmailUsersModal from '../components/EmailUsersModal';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<FirebaseUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'createdAt', direction: 'desc' });
  const [userFilter, setUserFilter] = useState<'all'|'active'|'loginToday'|'createdToday'>('all');
  const [selectedUser, setSelectedUser] = useState<FirebaseUser | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const usersList = usersSnapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      })) as FirebaseUser[];
      setUsers(usersList);
      
      // Update selectedUser if it exists
      if (selectedUser) {
        const updatedSelectedUser = usersList.find(user => user.uid === selectedUser.uid);
        if (updatedSelectedUser) {
          setSelectedUser(updatedSelectedUser);
        }
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshSelectedUser = async () => {
    if (!selectedUser) return;
    
    try {
      const userDocRef = doc(db, 'users', selectedUser.uid);
      const userSnapshot = await getDoc(userDocRef);
      
      if (userSnapshot.exists()) {
        const updatedUser = { uid: userSnapshot.id, ...userSnapshot.data() } as FirebaseUser;
        setSelectedUser(updatedUser);
        // Also update in the users array
        setUsers(prevUsers => 
          prevUsers.map(user => user.uid === updatedUser.uid ? updatedUser : user)
        );
      }
    } catch (error) {
      console.error('Error refreshing selected user:', error);
    }
  };

  const handleDeleteUser = async (uid: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteDoc(doc(db, 'users', uid));
        setUsers(users.filter(u => u.uid !== uid));
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Failed to delete user');
      }
    }
  };

  const handleToggleUserStatus = async (uid: string, currentStatus: boolean) => {
    try {
      await updateDoc(doc(db, 'users', uid), {
        isActive: !currentStatus
      });
      setUsers(users.map(u => 
        u.uid === uid ? { ...u, isActive: !currentStatus } : u
      ));
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Failed to update user status');
    }
  };

  const todayStr = new Date().toLocaleDateString();
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users;
    if (userFilter === 'active') {
      filtered = users.filter(u => Array.isArray(u.unlockedSpecialties) && u.unlockedSpecialties.length > 0);
    } else if (userFilter === 'loginToday') {
      filtered = users.filter(u => u.lastLoginAt && u.lastLoginAt.toDate && u.lastLoginAt.toDate().toLocaleDateString() === todayStr);
    } else if (userFilter === 'createdToday') {
      filtered = users.filter(u => u.createdAt && u.createdAt.toDate && u.createdAt.toDate().toLocaleDateString() === todayStr);
    }
    const term = search.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter(u => {
        const name = (u as any).name || '';
        const specialties = Array.isArray(u.unlockedSpecialties) ? u.unlockedSpecialties.join(' ') : '';
        return [name, u.email, u.uid, specialties]
          .filter(Boolean)
          .some(val => String(val).toLowerCase().includes(term));
      });
    }
    const sorted = [...filtered].sort((a, b) => {
      const { key, direction } = sortConfig;
      const dir = direction === 'asc' ? 1 : -1;
      const aVal = (a as any)[key];
      const bVal = (b as any)[key];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (aVal?.toDate && bVal?.toDate) {
        const diff = aVal.toDate().getTime() - bVal.toDate().getTime();
        return diff * dir;
      }
      if (Array.isArray(aVal) && Array.isArray(bVal)) {
        return (aVal.length - bVal.length) * dir;
      }
      return String(aVal).localeCompare(String(bVal)) * dir;
    });
    return sorted;
  }, [users, search, sortConfig, userFilter, todayStr]);

  const toggleSort = (key: string) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      // Default direction for different columns
      if (key === 'createdAt' || key === 'lastLoginAt') {
        return { key, direction: 'desc' }; // Newest first for dates
      } else if (key === 'name' || key === 'email') {
        return { key, direction: 'asc' }; // A-Z for text
      } else {
        return { key, direction: 'desc' }; // Default to desc for other columns
      }
    });
  };

  const sortIndicator = (key: string) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const activeUserCount = users.filter(u => Array.isArray(u.unlockedSpecialties) && u.unlockedSpecialties.length > 0).length;
  const inactiveUserCount = users.filter(u => u.lastLoginAt && u.lastLoginAt.toDate && u.lastLoginAt.toDate().toLocaleDateString() === todayStr).length;

  return (
    <div className="operation-content" style={selectedUser ? {overflow: 'hidden', height: '100vh'} : {}}>
      <h2>User Management</h2>
      
      {/* Analytics Cards */}
      <div className="analytics-grid" style={{marginBottom: '30px'}}>
        <div
          className={`analytics-card${userFilter==='all' ? ' active' : ''}`}
          style={{cursor:'pointer', border: userFilter==='all' ? '2px solid #64b5f6' : undefined}}
          onClick={() => setUserFilter(userFilter==='all' ? 'all' : 'all')}
        >
          <h3>Total Users</h3>
          <div className="analytics-number">{users.length}</div>
        </div>
        <div
          className={`analytics-card${userFilter==='active' ? ' active' : ''}`}
          style={{cursor:'pointer', border: userFilter==='active' ? '2px solid #64b5f6' : undefined}}
          onClick={() => setUserFilter(userFilter==='active' ? 'all' : 'active')}
        >
          <h3>Active Users</h3>
          <div className="analytics-number">{activeUserCount}</div>
        </div>
        <div
          className={`analytics-card${userFilter==='loginToday' ? ' active' : ''}`}
          style={{cursor:'pointer', border: userFilter==='loginToday' ? '2px solid #64b5f6' : undefined}}
          onClick={() => setUserFilter(userFilter==='loginToday' ? 'all' : 'loginToday')}
        >
          <h3>Daily User Count</h3>
          <div className="analytics-number">{inactiveUserCount}</div>
        </div>
        <div
          className={`analytics-card${userFilter==='createdToday' ? ' active' : ''}`}
          style={{cursor:'pointer', border: userFilter==='createdToday' ? '2px solid #64b5f6' : undefined}}
          onClick={() => setUserFilter(userFilter==='createdToday' ? 'all' : 'createdToday')}
        >
          <h3>Users Created Today</h3>
          <div className="analytics-number">{
            users.filter(u => u.createdAt && u.createdAt.toDate && u.createdAt.toDate().toLocaleDateString() === todayStr).length
          }</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex', 
        justifyContent: 'flex-end', 
        marginBottom: '20px',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => setEmailModalOpen(true)}
          style={{
            background: '#4caf50',
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#45a049';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#4caf50';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <span>📧</span>
          Email Users
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading users...</div>
      ) : (
        <div className="users-table-wrapper">
          <div style={{marginBottom: '16px', display:'flex', flexWrap:'wrap', gap:'12px', alignItems:'center'}}>
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                padding: '10px 14px',
                borderRadius: 6,
                border: '5px solid #949494ff',
                background: '#585757ff',
                color: '#f3f3f3ff',
                flex: '1 1 260px'
              }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{
                  background:'#444',
                  color:'#eee',
                  border:'1px solid #555',
                  padding:'10px 16px',
                  borderRadius:6,
                  cursor:'pointer'
                }}
              >Clear</button>
            )}
            <span style={{marginLeft:'auto', color:'#64b5f6', fontWeight:600, fontSize:'16px', paddingRight:'18px'}}>
              {filteredAndSortedUsers.length} result{filteredAndSortedUsers.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th style={{cursor:'pointer'}} onClick={() => toggleSort('name')}>User{sortIndicator('name')}</th>
                <th style={{cursor:'pointer'}} onClick={() => toggleSort('email')}>Email{sortIndicator('email')}</th>
                <th style={{cursor:'pointer'}} onClick={() => toggleSort('uid')}>UID{sortIndicator('uid')}</th>
                <th style={{cursor:'pointer'}} onClick={() => toggleSort('createdAt')}>Created On{sortIndicator('createdAt')}</th>
                <th style={{cursor:'pointer'}} onClick={() => toggleSort('lastLoginAt')}>Last Login{sortIndicator('lastLoginAt')}</th>
                <th style={{cursor:'pointer'}} onClick={() => toggleSort('unlockedSpecialties')}>Specialties{sortIndicator('unlockedSpecialties')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="no-data">
                    No users found. Users will appear here when they sign up.
                  </td>
                </tr>
              ) : (
                filteredAndSortedUsers.map((userData) => (
                  <tr key={userData.uid} style={{cursor:'pointer'}} onClick={() => setSelectedUser(userData)}>
                    <td data-label="User">{userData.name || 'N/A'}</td>
                    <td data-label="Email">{userData.email}</td>
                    <td data-label="UID">{userData.uid || 'N/A'}</td>
                    <td data-label="Created On">
                      {userData.createdAt && userData.createdAt.toDate
                        ? userData.createdAt.toDate().toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td data-label="Last Login">
                      {userData.lastLoginAt && userData.lastLoginAt.toDate ? (
                        <span style={{display:'flex', flexDirection:'column'}}>
                          <span>{userData.lastLoginAt.toDate().toLocaleDateString()}</span>
                          <span>{userData.lastLoginAt.toDate().toLocaleTimeString()}</span>
                        </span>
                      ) : (
                        'Never'
                      )}
                    </td>
                    <td data-label="Specialties">
                      {Array.isArray(userData.unlockedSpecialties) && userData.unlockedSpecialties.length > 0 ? (
                        <ul style={{margin: 0, padding: 0, listStyle: 'none'}}>
                          {userData.unlockedSpecialties.map((spec, idx, arr) => {
                            const formatted = spec
                              .split('-')
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ');
                            return (
                              <li key={idx}>
                                {formatted}
                                {idx < arr.length - 1 ? ',' : ''}
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        'None'
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* User Management Modal */}
      {selectedUser && (
        <SelectedUserManagementModal 
          user={selectedUser} 
          onClose={() => setSelectedUser(null)}
          onUserUpdate={refreshSelectedUser}
        />
      )}
      
      {/* Email Users Modal */}
      {emailModalOpen && (
        <EmailUsersModal 
          users={users} 
          onClose={() => setEmailModalOpen(false)}
        />
      )}
    </div>
  );
};

export default UserManagement;